// src/panels/SettingsPanels.jsx
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, TextField, Button, Grid, ToggleButton, ToggleButtonGroup, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { SectionHeader, isDeadlinePassed } from '../components/SharedComponents';
import { COVER_OPTIONS, GENDERS, HAIR_COLORS, HAIR_STYLES, EYE_COLORS, RACES, AGE_GROUPS } from '../data/constants';
import api from '../services/api';
import StripePaymentWrapper from '../components/StripePaymentWrapper';
import useToast from '../hooks/useToast';
import useOrderMedia from '../hooks/useOrderMedia';
import CartoonPlaceholder from '../components/CartoonPlaceholder';
import { Check } from '@mui/icons-material';
import GlobalStyles from '@mui/material/GlobalStyles';

const clamp = (value, min, max) =>
  Math.min(Math.max(Number(value), min), max);

/**
 * Fixed-size frame + crop_rect + aspect-fit/contain.
 *
 * Important:
 * - The OUTER frame always keeps one fixed aspect ratio.
 * - crop_rect is applied against the ORIGINAL image.
 * - The resulting cropped region is then aspect-fitted ("contain")
 *   and centered inside the fixed outer frame.
 * - No stretching.
 */

function CoverFonts() {
  return (
    <GlobalStyles
      styles={`
        @font-face {
          font-family: "Bernoru Semi Condensed";
          src: url("/fonts/Bernoru-Semi-Condensed.woff2") format("woff2");
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: "Bernoru Black Condensed";
          src: url("/fonts/Bernoru-BlackCondensed.woff2") format("woff2");
          font-weight: 800;
          font-style: normal;
          font-display: swap;
        }
      `}
    />
  );
}

function resolveTemplatePhotoCrop(option, imageSize, spec, targetAspect) {
  const full = { x: 0, y: 0, width: 1, height: 1 };
  const normalize = (input, allowPixels = false) => {
    let r = input;
    try { if (typeof r === 'string') r = JSON.parse(r); } catch { return null; }
    if (!r) return null;
    let { x, y } = r;
    let width = r.width ?? r.w, height = r.height ?? r.h;
    [x, y, width, height] = [x, y, width, height].map(Number);
    if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;
    if (allowPixels && Math.max(x, y, width, height) > 1) {
      if (!imageSize) return null;
      x /= imageSize.width; width /= imageSize.width;
      y /= imageSize.height; height /= imageSize.height;
    }
    if (x < 0 || y < 0 || x >= 1 || y >= 1 || x + width > 1.000001 || y + height > 1.000001) return null;
    return { x, y, width: Math.min(width, 1 - x), height: Math.min(height, 1 - y) };
  };
  const suppliedCrop = normalize(option?.crop_rect);
  if (spec?.fit !== 'saliency_cover' || !imageSize) return { crop: suppliedCrop || full, error: null };
  const keys = [spec.saliency?.preferred_rect, ...(spec.saliency?.fallback_rects || [])].filter(Boolean);
  const focus = keys.map(key => normalize(option?.[key] ?? option?.saliency?.[key], true)).find(Boolean);
  if (!focus) {
    // A server-computed crop_rect is authoritative when saliency metadata is omitted.
    return { crop: suppliedCrop || full, error: !suppliedCrop && spec.saliency?.required
      ? 'This photo needs a crop_rect or saliency rectangle from the API.' : null };
  }
  const padding = Math.max(0, Number(spec.crop_policy?.padding_pct) || 0) / 100;
  const left = Math.max(0, focus.x - focus.width * padding);
  const top = Math.max(0, focus.y - focus.height * padding);
  const right = Math.min(1, focus.x + focus.width * (1 + padding));
  const bottom = Math.min(1, focus.y + focus.height * (1 + padding));
  const ratio = targetAspect * imageSize.height / imageSize.width;
  let width = Math.max(right - left, (bottom - top) * ratio);
  let height = width / ratio;
  if (width > 1.000001 || height > 1.000001) return {
    crop: full, error: 'The required photo region cannot fit this slot without being cut off.',
  };
  if (spec.crop_policy?.tightest_crop === false) {
    const scale = Math.min(1 / width, 1 / height);
    width *= scale; height *= scale;
  }
  return { crop: {
    x: Math.max(0, Math.min(1 - width, (left + right - width) / 2)),
    y: Math.max(0, Math.min(1 - height, (top + bottom - height) / 2)),
    width, height,
  }, error: null };
}

function CoverCropImage({ option, frameAspectRatio = 1, fit = 'contain', showLoader = true, photoSpec = null }) {
  const imageUrl = option?.image_url || option?.display_url || null;
  const [imageSize, setImageSize] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageSize(null);
    setImageFailed(false);
    if (!imageUrl) return;
    let active = true;
    const probe = new Image();
    probe.onload = () => {
      if (active) setImageSize({
        width: probe.naturalWidth,
        height: probe.naturalHeight,
      });
    };
    probe.onerror = () => { if (active) setImageFailed(true); };
    probe.src = imageUrl;
    return () => { active = false; };
  }, [imageUrl]);

  const cropResult = useMemo(
    () => resolveTemplatePhotoCrop(option, imageSize, photoSpec, frameAspectRatio),
    [option, imageSize, photoSpec, frameAspectRatio]
  );
  const crop = cropResult.crop;

  const cropAspect = imageSize?.width && imageSize?.height
    ? (imageSize.width * crop.width) / (imageSize.height * crop.height)
    : null;
  const frameAspect = Number(frameAspectRatio) > 0 ? Number(frameAspectRatio) : 1;
  // For a wide crop: cover scales its height to 100% and lets width overflow.
  // For a tall crop: cover scales its width to 100% and lets height overflow.
  const viewportWidth = cropAspect
    ? (fit === 'cover'
      ? Math.max(100, (cropAspect / frameAspect) * 100)
      : Math.min(100, (cropAspect / frameAspect) * 100))
    : 100;
  const viewportHeight = cropAspect
    ? (fit === 'cover'
      ? Math.max(100, (frameAspect / cropAspect) * 100)
      : Math.min(100, (frameAspect / cropAspect) * 100))
    : 100;

  return (
    <Box sx={{
      position: 'relative', width: '100%', height: '100%',
      overflow: 'hidden', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      bgcolor: '#eef2f6', pointerEvents: 'none',
    }}>
      {cropResult.error ? <Typography role="alert" sx={{ fontSize: 12, p: 1, color: 'error.main' }}>{cropResult.error}</Typography> : imageUrl && imageSize?.width && imageSize?.height && !imageFailed ? (
        <Box sx={{
          position: 'relative', width: `${viewportWidth}%`,
          height: `${viewportHeight}%`, flexShrink: 0, overflow: 'hidden',
        }}>
          <Box component="img" src={imageUrl} alt="" draggable={false}
            onError={() => setImageFailed(true)}
            sx={{
              position: 'absolute',
              width: `${100 / crop.width}%`,
              height: `${100 / crop.height}%`,
              left: `${(-crop.x * 100) / crop.width}%`,
              top: `${(-crop.y * 100) / crop.height}%`,
              maxWidth: 'none', maxHeight: 'none',
              display: 'block', pointerEvents: 'none',
            }}
          />
        </Box>
      ) : imageUrl && !imageFailed
        ? (showLoader ? <CircularProgress size={24} /> : null)
        : <Typography sx={{ fontSize: 38, opacity: 0.55 }}>📚</Typography>}
    </Box>
  );
}

// Template values are converted to source pixels; the preview scales as one page.
function CoverNameText({ text, slot, font, overflow, pageSize, pixelsPerPoint, onError }) {
  const [layout, setLayout] = useState(null);
  const family = font?.family || 'sans-serif';
  const weight = Number(font?.weight) || 400;
  const defaultSize = Number(font?.default_size) || 45;
  const minSize = Number(font?.min_size) || defaultSize;
  const maxSize = Math.max(minSize, Number(font?.max_size) || defaultSize);
  const startSize = Math.min(maxSize, Math.max(minSize, defaultSize));

  useEffect(() => {
    let cancelled = false;
    setLayout(null);
    onError(null);
    if (!text || !slot || slot.w <= 0 || slot.h <= 0) return;
    async function measure() {
      const context = document.createElement('canvas').getContext('2d');
      if (!context) return;
      const fontCss = size => `${weight} ${size * pixelsPerPoint}px ${JSON.stringify(family)}`;
      try { await document.fonts?.load(fontCss(startSize), text); } catch { /* Use browser fallback. */ }
      if (cancelled) return;
      const at = size => {
        context.font = fontCss(size);
        const m = context.measureText(text);
        const left = Math.max(0, m.actualBoundingBoxLeft || 0);
        const right = Math.max(m.width, m.actualBoundingBoxRight || m.width);
        const ascent = m.actualBoundingBoxAscent ?? size * pixelsPerPoint * 0.8;
        const descent = m.actualBoundingBoxDescent ?? size * pixelsPerPoint * 0.2;
        return { size, left, right, ascent, descent, fits: left + right <= slot.w && ascent + descent <= slot.h };
      };
      let result = at(startSize);
      if (!result.fits && at(minSize).fits) {
        let lo = minSize, hi = startSize;
        for (let i = 0; i < 18; i++) {
          const mid = (lo + hi) / 2;
          if (at(mid).fits) lo = mid; else hi = mid;
        }
        result = at(lo);
      } else if (!result.fits) result = at(minSize);
      if (cancelled) return;
      setLayout(result);
      onError(!result.fits && (overflow?.mode || 'error') === 'error'
        ? (overflow?.error_message || 'Student name does not fit inside the configured cover name rect.')
        : null);
    }
    measure();
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      cancelled = true;
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [text, slot?.w, slot?.h, family, weight, startSize, minSize, pixelsPerPoint, overflow?.mode, overflow?.error_message, onError]);

  if (!text || !slot || !layout) return null;
  const inkWidth = layout.left + layout.right;
  const textX = font?.align === 'left' ? layout.left
    : font?.align === 'right' ? slot.w - layout.right
    : (slot.w - inkWidth) / 2 + layout.left;
  const inkHeight = layout.ascent + layout.descent;
  const textY = font?.valign === 'top' ? layout.ascent
    : font?.valign === 'bottom' ? slot.h - layout.descent
    : (slot.h - inkHeight) / 2 + layout.ascent;
  return (
    <svg viewBox={`0 0 ${slot.w} ${slot.h}`} aria-label={text}
      style={{ position: 'absolute', left: `${slot.x / pageSize.width * 100}%`,
        top: `${slot.y / pageSize.height * 100}%`, width: `${slot.w / pageSize.width * 100}%`,
        height: `${slot.h / pageSize.height * 100}%`, overflow: 'hidden', zIndex: 2 }}>
      <text x={textX} y={textY} fill={font?.color || '#872954'}
        fontFamily={family} fontWeight={weight} fontSize={layout.size * pixelsPerPoint}>
        {text}
      </text>
    </svg>
  );
}

function CoverTemplatePreview({ option, templateUrl, overlayUrl, slot, templateSize,
  studentName, nameSlot, nameFont, photoSpec, nameOverflow, pixelsPerPoint, onNameError }) {
  const { width, height } = templateSize;
  const border = photoSpec?.border;
  const borderWidth = border?.enabled ? Math.max(0, Number(border.width_px) || 0) : 0;
  const inset = border?.inset_photo_by_border ? borderWidth : 0;
  const photoWidth = Math.max(1, slot.w - 2 * inset);
  const photoHeight = Math.max(1, slot.h - 2 * inset);
  const layerStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' };
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {templateUrl && <Box component="img" src={templateUrl} alt="" sx={layerStyle} />}
      <Box sx={{ position: 'absolute', left: `${slot.x / width * 100}%`, top: `${slot.y / height * 100}%`,
        width: `${slot.w / width * 100}%`, height: `${slot.h / height * 100}%`,
        bgcolor: border?.enabled ? (border.color || '#fff') : 'transparent' }}>
        <Box sx={{ position: 'absolute', left: `${inset / slot.w * 100}%`, top: `${inset / slot.h * 100}%`,
          width: `${photoWidth / slot.w * 100}%`, height: `${photoHeight / slot.h * 100}%`, overflow: 'hidden' }}>
          <CoverCropImage option={option} frameAspectRatio={photoWidth / photoHeight}
            fit={photoSpec?.fit === 'contain' ? 'contain' : 'cover'} photoSpec={photoSpec} />
        </Box>
        {borderWidth > 0 && !inset && <svg viewBox={`0 0 ${slot.w} ${slot.h}`} style={{ ...layerStyle, overflow: 'visible' }}>
          <rect x={borderWidth / 2} y={borderWidth / 2} width={Math.max(0, slot.w - borderWidth)}
            height={Math.max(0, slot.h - borderWidth)} fill="none" stroke={border.color || '#fff'} strokeWidth={borderWidth} />
        </svg>}
      </Box>
      <CoverNameText text={studentName} slot={nameSlot} font={nameFont} overflow={nameOverflow}
        pageSize={templateSize} pixelsPerPoint={pixelsPerPoint} onError={onNameError} />
      {overlayUrl && <Box component="img" src={overlayUrl} alt="" sx={{ ...layerStyle, zIndex: 3 }} />}
    </Box>
  );
}

// Python templates use top-left coordinates in inches. API rects can use
// {x, y, width, height}; our local preview uses pixels {x, y, w, h}.
function resolveCoverSlot(rect, size, page, units, fallback) {
  if (!rect) return fallback;
  const pageWidth = Number(page?.width);
  const pageHeight = Number(page?.height);
  const factorX = units === 'in' && pageWidth > 0 ? size.width / pageWidth : 1;
  const factorY = units === 'in' && pageHeight > 0 ? size.height / pageHeight : 1;
  const resolved = {
    x: Number(rect.x) * factorX,
    y: Number(rect.y) * factorY,
    w: Number(rect.w ?? rect.width) * factorX,
    h: Number(rect.h ?? rect.height) * factorY,
  };
  return Object.values(resolved).every(Number.isFinite) ? resolved : fallback;
}

function coverPhotoUrl(value) {
  return typeof value === 'string' ? value
    : value?.image_url || value?.display_url || value?.url || null;
}

// Keep crop/saliency metadata when the API supplies cover_photo as an object.
function coverPhotoOption(data) {
  const photo = data?.cover_photo;
  const url = coverPhotoUrl(photo);
  if (!url) return null;
  return {
    ...(typeof photo === 'object' ? photo : {}),
    media_item_id: data.selected_media_item_id || data.project?.cover_media_item_id
      || photo?.media_item_id || 'api-cover-photo',
    image_url: url,
    crop_rect: photo?.crop_rect || data.crop_rect,
    saliency: photo?.saliency || data.saliency,
    is_selected: true,
  };
}

// Demo data: put mock-kids.png in your app's public directory.
// Each crop_rect selects one quadrant from the 2 × 2 image.
const MOCK_COVER_OPTIONS = [
  { media_item_id: 'mock-kid-1', image_url: '/mock-kid-1.jpg', crop_rect: { x: 0, y: 0, width: 1, height: 1 }, is_recommended: true },
  { media_item_id: 'mock-kid-2', image_url: '/mock-kid-2.jpg', crop_rect: { x: 0, y: 0, width: 1, height: 1 } },
  { media_item_id: 'mock-kid-3', image_url: '/mock-kid-3.jpg', crop_rect: { x: 0, y: 0, width: 1, height: 1 } },
  { media_item_id: 'mock-kid-4', image_url: '/mock-kid-4.jpg', crop_rect: { x: 0, y: 0, width: 1, height: 1 } },
];
// Font sizes are typographic points; mock slot coordinates are pixels at 300 DPI.
const MOCK_FONT = { family: 'Bernoru Black Condensed', color: '#3e1601', align: 'center', default_size: 42, size_units: 'pt', weight: 800 };
const MOCK_STUDENT_NAME_SLOT = { x: 143, y: 1706, w: 1714, h: 200 };
const MOCK_COVER_PHOTO_SLOT = { x: 630, y: 698, w: 738, h: 738 };
const MOCK_COVER_TEMPLATE_SIZE = { width: 2000, height: 2000 };


export const DEFAULT_BOOK_COVER_TEMPLATE = {
  "template_key": "square_preschool_yearbook_cover_2026",
  "template_type": "cover",
  "coordinate_origin": "top_left",
  "units": "in",
  "dpi": 300,
  "page": {
    "width": 8.5,
    "height": 8.5
  },
  "sku_match": {
    "cover_type": "square",
    "trim_size": "8x8"
  },
  "asset_bindings": {
    "background": {
      "binding_key": "cover",
      "element_key": "background",
      "variant_key": "square_preschool_yearbook_2026",
      "required": true
    },
    "overlay": {
      "binding_key": "cover",
      "element_key": "overlay",
      "variant_key": "square_preschool_yearbook_2026",
      "required": false
    }
  },
  "slots": {
    "cover_photo": {
      "type": "image",
      "source": "project.cover_media_item_id",
      "rect": {
        "x": 2.68,
        "y": 2.97,
        "width": 3.14,
        "height": 3.14
      },
      "border": {
        "enabled": false
      },
      "fit": "saliency_cover",
      "saliency": {
        "required": true,
        "preferred_rect": "union_rect_json",
        "fallback_rects": [
          "object_rect_json",
          "attention_rect_json"
        ],
        "missing_error_code": "saliency_missing"
      },
      "crop_policy": {
        "must_include_saliency": true,
        "target_aspect_from_slot": true,
        "tightest_crop": true,
        "padding_pct": 0.0
      }
    },
    "student_name": {
      "type": "text",
      "source": "person.first_name + person.middle_name + person.last_name",
      "filename_source": "person.first_name + person.last_name",
      "rect": {
        "x": 0.68,
        "y": 7.25,
        "width": 7.28,
        "height": 0.77
      },
      "font": {
        "family": "Bernoru Black Condensed",
        "default_size": 45,
        "min_size": 28,
        "max_size": 45,
        "color": "#334c12",
        "align": "center",
        "valign": "middle"
      },
      "overflow": {
        "mode": "error",
        "error_code": "name_overflow",
        "error_message": "Student name does not fit inside the configured cover name rect."
      }
    }
  },
  "output": {
    "bucket_role": "public_assets",
    "bucket_name_template": "{app_id}-cdn",
    "prefix": "preschool_pdfs",
    "filename_template": "{first_name}_{last_name}_{project_id}_cover.pdf",
    "filename_case": "lowercase",
    "content_type": "application/pdf",
    "store_on_order_item_path": "print_artifacts.cover"
  }
};

export function PanelCover({
  child,
  onSave,
  order_item_id,
  bookConfig,
  mockMode = false,
  coverTemplate = null,
  coverAssets = null,
  coverData = null,
  coverTemplateUrl = '/cover-template.png',
  coverPhotoSlot = MOCK_COVER_PHOTO_SLOT,
  coverNameSlot = MOCK_STUDENT_NAME_SLOT,
  coverTemplateSize = MOCK_COVER_TEMPLATE_SIZE,
}) {
  const toast = useToast();

  // Use the project tied to the authenticated parent's child/order.
  const projectId =
    bookConfig?.project_id ||
    child?.project_id ||
    child?.projectId ||
    null;

  const [coverOptions, setCoverOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [coverStatus, setCoverStatus] = useState(null);
  const [selectedBy, setSelectedBy] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [totalOptions, setTotalOptions] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectingId, setSelectingId] = useState(null);
  const [error, setError] = useState(null);
  const [remainingLoaded, setRemainingLoaded] = useState(false);
  const [apiCoverData, setApiCoverData] = useState(null);
  const currentCoverData = {
    ...(coverData || {}),
    ...(apiCoverData || {}),
  };
  const templateConfig = coverTemplate || currentCoverData.cover_template
    || currentCoverData.template || (currentCoverData.template_type === 'cover' ? currentCoverData : null)
    || DEFAULT_BOOK_COVER_TEMPLATE;
  const templateSlots = templateConfig.slots;
  const templatePage = templateConfig.page;
  const templateUnits = templateConfig.units || 'in';
  const templateDpi = Number(templateConfig.dpi) || 300;
  const pageFactor = templateUnits === 'in' ? templateDpi : 1;
  const templateSize = {
    width: Number(templatePage.width) * pageFactor,
    height: Number(templatePage.height) * pageFactor,
  };
  const photoSpec = templateSlots.cover_photo;
  const nameSpec = templateSlots.student_name;
  const photoSlot = resolveCoverSlot(photoSpec.rect, templateSize, templatePage, templateUnits, coverPhotoSlot);
  const nameSlot = resolveCoverSlot(nameSpec.rect, templateSize, templatePage, templateUnits, coverNameSlot);
  const person = currentCoverData.person || child || {};
  const studentName = [
    currentCoverData.student_name,
    [person.first_name, person.middle_name, person.last_name]
      .filter(value => typeof value === 'string' && value.trim()).join(' '),
    person.student_name,
    person.name,
    mockMode ? 'Maya Patel' : '',
  ].find(value => typeof value === 'string' && value.trim())?.trim() || '';
  // asset_bindings identifies assets; the backend must also resolve their URLs.
  const assets = coverAssets || currentCoverData.assets || {};
  const templateUrl = coverPhotoUrl(assets.background) || currentCoverData.cover_template_url
    || currentCoverData.background_url || coverTemplateUrl;
  const overlayUrl = coverPhotoUrl(assets.overlay) || currentCoverData.overlay_url;
  const nameFont = nameSpec.font;
  const pixelsPerPoint = nameFont?.size_units === 'px' ? 1 : templateDpi / 72;
  const [nameError, setNameError] = useState(null);

  const isViewOnly = useMemo(
    () => isDeadlinePassed(bookConfig?.content_deadline),
    [bookConfig?.content_deadline]
  );

  const getResponseOptions = useCallback((data) => {
    if (Array.isArray(data?.options)) {
      return data.options;
    }

    if (Array.isArray(data?.candidates)) {
      return data.candidates;
    }

    return [];
  }, []);

  const applyResponseMetadata = useCallback(
    (data) => {
      const responseOptions = getResponseOptions(data);

      setCoverStatus(data?.status ?? null);
      setSelectedBy(data?.selected_by ?? null);
      setIsLocked(Boolean(data?.is_locked));

      setTotalOptions(
        Number(
          data?.total_options ??
            data?.total_candidates ??
            responseOptions.length ??
            0
        )
      );

      if (data?.selected_media_item_id || data?.project?.cover_media_item_id) {
        setSelected(data.selected_media_item_id || data.project.cover_media_item_id);
        return;
      }

      const selectedOption =
        responseOptions.find((item) => item?.is_selected);

      if (selectedOption?.media_item_id) {
        setSelected(selectedOption.media_item_id);
      }
    },
    [getResponseOptions]
  );

  // ============================================================
  // Initial first 5
  // ============================================================

  const fetchInitialCovers = useCallback(async () => {
    if (mockMode) {
      setApiCoverData(null);
      setCoverOptions(MOCK_COVER_OPTIONS);
      setSelected(MOCK_COVER_OPTIONS[0].media_item_id);
      setTotalOptions(MOCK_COVER_OPTIONS.length);
      setCoverStatus('ready');
      setLoading(false);
      return;
    }
    if (!projectId) {
      setLoading(false);
      setCoverOptions([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setRemainingLoaded(false);

      const data = await api._get(
        `/parent/projects/${projectId}/cover-selection`
      );
      setApiCoverData(data);

      const responseOptions = getResponseOptions(data);
      const fallbackPhoto = coverPhotoOption(data);
      const options = responseOptions.length ? responseOptions : fallbackPhoto
        ? [fallbackPhoto]
        : [];

      if (!options.length) {
        setCoverOptions([]);
        setSelected(null);

        setTotalOptions(
          Number(
            data?.total_options ??
              data?.total_candidates ??
              0
          )
        );

        setCoverStatus(data?.status ?? null);
        return;
      }

      setCoverOptions(options);
      applyResponseMetadata(data);
      if (!data?.selected_media_item_id && !data?.project?.cover_media_item_id && !options.some(item => item.is_selected)) {
        setSelected(options[0]?.media_item_id || null);
      }
    } catch (err) {
      console.error('Cover selection fetch error:', err);

      setError(
        err?.message ||
          'Unable to load cover options.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    projectId,
    mockMode,
    coverData,
    getResponseOptions,
    applyResponseMetadata,
  ]);

  useEffect(() => {
    fetchInitialCovers();
  }, [fetchInitialCovers]);

  // ============================================================
  // Show more
  // Existing + remaining
  // ============================================================

  const handleShowMore = async () => {
    if (
      !projectId ||
      loadingMore ||
      remainingLoaded
    ) {
      return;
    }

    try {
      setLoadingMore(true);
      setError(null);

      const data = await api._get(
        `/parent/projects/${projectId}/cover-selection?view=remaining`
      );

      const incoming = getResponseOptions(data);

      setCoverOptions((oldOptions) => {
        const existingIds = new Set(
          oldOptions.map(
            (item) => item.media_item_id
          )
        );

        const newOptions = incoming.filter(
          (item) =>
            item?.media_item_id &&
            !existingIds.has(item.media_item_id)
        );

        return [
          ...oldOptions,
          ...newOptions,
        ];
      });

      applyResponseMetadata(data);

      // "remaining" returns all remaining candidates,
      // so Show More can disappear afterwards.
      setRemainingLoaded(true);
    } catch (err) {
      console.error('Show more cover error:', err);

      toast.error(
        err?.message ||
          'Unable to load more cover photos.'
      );
    } finally {
      setLoadingMore(false);
    }
  };

  // ============================================================
  // Select cover
  // ============================================================

  const handleSelect = async (mediaItemId) => {
    if (
      !projectId ||
      selectingId ||
      selected === mediaItemId ||
      coverStatus === 'awaiting_probe'
    ) {
      return;
    }

    const previousSelected = selected;

    // Optimistic UI.
    setSelected(mediaItemId);
    setSelectingId(mediaItemId);

    setCoverOptions((oldOptions) =>
      oldOptions.map((item) => ({
        ...item,
        is_selected:
          item.media_item_id === mediaItemId,
      }))
    );

    if (mockMode) {
      setSelectingId(null);
      return;
    }

    try {
      /*
       * Use your existing api helper so auth/base URL stay
       * consistent with the GET requests.
       *
       * If your api helper names POST differently, change
       * only this call.
       */
      const data = await api._post(
        `/parent/projects/${projectId}/cover-selection/select`,
        {
          media_item_id: mediaItemId,
        }
      );
      if (data?.student_name || data?.cover_photo || data?.cover_template_url || data?.cover_template) {
        setApiCoverData(previous => ({ ...previous, ...data }));
      }

      const newSelectedId =
        data?.selected_media_item_id ||
        mediaItemId;

      applyResponseMetadata(data);

      /*
       * Do NOT replace the array after Show More.
       * Just move is_selected.
       */
      setCoverOptions((oldOptions) =>
        oldOptions.map((item) => ({
          ...item,
          is_selected:
            item.media_item_id === newSelectedId,
        }))
      );

      toast.success('Cover saved.');

      if (onSave) {
        onSave(newSelectedId, data);
      }
    } catch (err) {
      console.error('Cover selection error:', err);

      // Roll back optimistic selection.
      setSelected(previousSelected);

      setCoverOptions((oldOptions) =>
        oldOptions.map((item) => ({
          ...item,
          is_selected:
            item.media_item_id === previousSelected,
        }))
      );

      /*
       * If your api wrapper exposes status differently,
       * adjust this status lookup only.
       */
      const status =
        err?.status ||
        err?.response?.status;

      if (status === 400) {
        toast.error(
          'The cover options changed. Refreshing the latest options.'
        );

        await fetchInitialCovers();
        return;
      }

      if (status === 409) {
        setCoverStatus('awaiting_probe');

        toast.error(
          'Cover photos are still preparing.'
        );
        return;
      }

      if (status === 404) {
        setCoverOptions([]);
        setSelected(null);
        setTotalOptions(0);
        return;
      }

      toast.error(
        err?.message ||
          'Unable to save cover.'
      );
    } finally {
      setSelectingId(null);
    }
  };

  const selectedCoverObj = useMemo(() => {
    if (!coverOptions.length) {
      return null;
    }

    return (
      coverOptions.find(
        (item) =>
          item.media_item_id === selected
      ) ||
      coverOptions.find(
        (item) => item.is_selected
      ) ||
      coverOptions[0]
    );
  }, [coverOptions, selected]);

  const remainingCount = Math.max(
    totalOptions - coverOptions.length,
    0
  );

  const showMoreButton =
    !remainingLoaded &&
    totalOptions > coverOptions.length;

  const selectionDisabled = coverStatus === 'awaiting_probe';

  return (
    <Box>
       <CoverFonts />
      <SectionHeader
        title="Book Cover"
        subtitle={
          isViewOnly
            ? 'The deadline has passed. Your cover selection is locked for printing.'
            : `Choose a beautiful cover photo for ${
                child?.name || 'your child'
              }'s yearbook.`
        }
      />

      <Box sx={{ maxWidth: 800 }}>
        {nameError && <Alert severity="error" sx={{ mb: 2 }}>{nameError}</Alert>}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 4,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {coverStatus === 'awaiting_probe' && (
          <Alert
            severity="info"
            sx={{
              mb: 4,
              borderRadius: 2,
            }}
          >
            Cover photos are still preparing. Please try
            again shortly.
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : coverOptions.length === 0 ? (
          <Box
            sx={{
              py: 7,
              px: 3,
              textAlign: 'center',
              borderRadius: 4,
              bgcolor: '#f8fafc',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography
              sx={{
                fontSize: 42,
                mb: 1,
              }}
            >
              📚
            </Typography>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                color: '#0f172a',
              }}
            >
              Cover options are not available yet
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: 14,
                color: 'text.secondary',
              }}
            >
              Cover photos will appear here once they are
              ready.
            </Typography>
          </Box>
        ) : (
          <>
            {/* ====================================================
                LARGE SELECTED COVER

                Outer view:
                  fixed 1:1

                Inner cropped result:
                  contain + centered
               ==================================================== */}

            {selectedCoverObj && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 5,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 400,

                    aspectRatio: `${templateSize.width} / ${templateSize.height}`,
                    containerType: 'inline-size',

                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: '#eef2f6',

                    border: '1px solid',
                    borderColor: 'grey.200',

                    boxShadow:
                      '0 12px 32px rgba(0,0,0,0.15)',
                  }}
                >
                  <CoverTemplatePreview
                    option={selectedCoverObj}
                    templateUrl={templateUrl}
                    slot={photoSlot}
                    templateSize={templateSize}
                    studentName={studentName}
                    nameSlot={nameSlot}
                    nameFont={nameFont}
                    pixelsPerPoint={pixelsPerPoint}
                    photoSpec={photoSpec}
                    nameOverflow={nameSpec.overflow}
                    overlayUrl={overlayUrl}
                    onNameError={setNameError}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      top: 14,
                      left: 14,
                      zIndex: 2,

                      bgcolor: 'primary.main',
                      color: '#fff',
                      px: 1.5,
                      py: 0.6,
                      borderRadius: '20px',

                      fontSize: 12,
                      lineHeight: 1,
                      fontWeight: 700,

                      boxShadow:
                        '0 3px 10px rgba(0,0,0,0.18)',
                    }}
                  >
                    Selected
                  </Box>

                  {selectedCoverObj?.is_recommended && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        zIndex: 2,

                        bgcolor: '#FFB800',
                        color: '#000',
                        px: 1.5,
                        py: 0.6,
                        borderRadius: '20px',

                        fontSize: 12,
                        lineHeight: 1,
                        fontWeight: 700,

                        boxShadow:
                          '0 3px 10px rgba(0,0,0,0.18)',
                      }}
                    >
                      Our pick
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Typography
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: 18,
                color: '#0f172a',
              }}
            >
              Select photo{' '}
              <Box
                component="span"
                sx={{
                  color: '#7200a2',
                  fontWeight: 400,
                  fontSize: 14,
                }}
              >
                (which is added in cover)
              </Box>
            </Typography>

            {/* ====================================================
                THUMBNAILS

                Every tile has the exact same 1:1 outer frame.
                The crop result is contained and centered inside it.
               ==================================================== */}

            <Grid container spacing={2.5}>
              {coverOptions.map((option) => {
                const mediaItemId =
                  option.media_item_id;

                const isSelected =
                  selected === mediaItemId ||
                  option.is_selected;

                const isSelecting =
                  selectingId === mediaItemId;

                return (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    key={mediaItemId}
                  >
                    <Box
                      onClick={() => {
                        if (
                          !selectionDisabled &&
                          !selectingId
                        ) {
                          void handleSelect(mediaItemId);
                        }
                      }}
                      sx={{
                        width: '100%',

                        // FIXED IMAGE VIEW
                        aspectRatio: '1 / 1',

                        borderRadius: 4,
                        overflow: 'hidden',
                        position: 'relative',
                        bgcolor: '#eef2f6',

                        cursor:
                          selectionDisabled ||
                          selectingId
                            ? 'default'
                            : 'pointer',

                        border: '3px solid',

                        borderColor:
                          isSelected
                            ? 'primary.main'
                            : 'transparent',

                        boxShadow:
                          isSelected
                            ? '0 8px 24px rgba(25,118,210,0.30)'
                            : '0 2px 8px rgba(0,0,0,0.06)',

                        transition:
                          'all 0.25s cubic-bezier(0.4,0,0.2,1)',

                        opacity:
                          selectingId &&
                          !isSelecting
                            ? 0.65
                            : 1,

                        '&:hover':
                          selectionDisabled ||
                          selectingId
                            ? {}
                            : {
                                transform:
                                  'translateY(-4px)',

                                boxShadow:
                                  '0 12px 28px rgba(0,0,0,0.12)',
                              },
                      }}
                    >
                      <CoverCropImage
                        option={option}
                        frameAspectRatio={1}
                      />

                      {option.is_recommended && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 7,
                            bottom: 7,
                            zIndex: 2,

                            bgcolor: '#FFB800',
                            color: '#000',

                            px: 1.1,
                            py: 0.5,
                            borderRadius: '12px',

                            fontSize: 10,
                            lineHeight: 1,
                            fontWeight: 700,

                            boxShadow:
                              '0 2px 7px rgba(0,0,0,0.18)',
                          }}
                        >
                          Our pick
                        </Box>
                      )}

                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 2,

                            bgcolor: 'primary.main',
                            color: '#fff',

                            borderRadius: '50%',
                            width: 28,
                            height: 28,

                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',

                            boxShadow:
                              '0 2px 8px rgba(0,0,0,0.20)',

                            fontSize: 16,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </Box>
                      )}

                      {isSelecting && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 3,

                            bgcolor:
                              'rgba(255,255,255,0.50)',

                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CircularProgress size={30} />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* ====================================================
                SHOW MORE
               ==================================================== */}

            {showMoreButton && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 4,
                }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  sx={{
                    minWidth: 220,
                    minHeight: 48,
                    borderRadius: '24px',
                    px: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: 15,
                  }}
                >
                  {loadingMore ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ mr: 1.5 }}
                      />
                      Loading photos...
                    </>
                  ) : (
                    <>
                      Show more photos
                      {remainingCount > 0 &&
                        ` (${remainingCount})`}
                    </>
                  )}
                </Button>
              </Box>
            )}

            {totalOptions > 0 && (
              <Typography
                sx={{
                  textAlign: 'center',
                  mt: 2,
                  fontSize: 12,
                  color: 'text.secondary',
                }}
              >
                Showing {coverOptions.length} of{' '}
                {totalOptions} photos
              </Typography>
            )}

            {!isViewOnly &&
              coverOptions.length > 0 && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                    textAlign: 'center',
                    mt: 5,
                    mb: 2,
                  }}
                >
                  Changing covers after printing requires
                  additional payment.
                </Typography>
              )}
          </>
        )}
      </Box>
    </Box>
  );
}




// // ─── Cartoon ───
// const ResetIcon = ({ size = 11 }) => (
//   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
//     <path d="M3 3v5h5" />
//   </svg>
// );

// const CheckMarkIcon = ({ size = 12 }) => (
//   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
//     <polyline points="20 6 9 17 4 12" />
//   </svg>
// );
// // ------------------------------------------------
// let cartoonsCache = null;
// let cartoonsRequest = null;

// const getCartoonsOnce = async () => {
//   if (cartoonsCache) {
//     return cartoonsCache;
//   }

//   if (!cartoonsRequest) {
//     cartoonsRequest = api
//       .getCartoons({})
//       .then((result) => {
//         const cartoons = result?.cartoons || [];
//         cartoonsCache = cartoons;
//         return cartoons;
//       })
//       .catch((error) => {
//         // Allow retry if the request itself fails.
//         cartoonsRequest = null;
//         throw error;
//       });
//   }

//   return cartoonsRequest;
// };

// export function PanelCartoon({ child, onSave, bookConfig }) {
//   // const toast = useToast(); // Uncomment when implementing
//   const matcherRef = useRef(null);

//   // States for the selected cartoon and badge visibility
//   const [selectedImage, setSelectedImage] = useState(child?.cartoon_url || null);
//   const [isAiRecommended, setIsAiRecommended] = useState(true);

//   // Light Theme Palette matching the screenshot
//   const theme = {
//     bg: { elevated: "#ffffff", base: "#f9fafb", hover: "#f3f4f6" },
//     border: { subtle: "#e5e7eb", default: "#e5e7eb" },
//     text: { primary: "#111827", secondary: "#4b5563", muted: "#6b7280" },
//     primary: "#6366f1", // The purple from the screenshot
//     primaryLight: "#eef2ff",
//   };

//   // Form parameters for generation matching your screenshot's options
//   const [params, setParams] = useState({
//     ageGroup: 'preschool',
//     gender: 1,
//     skinTone: '',
//     hairColor: '',
//     hairStyle: '',
//     eyeColor: '',
//     freckles: ''
//   });

//   const [allCartoons, setAllCartoons] = useState([]);
//   const [isFetching, setIsFetching] = useState(false);

//   const isViewOnly = useMemo(
//     () => false, // Replace with your logic: isDeadlinePassed(bookConfig?.content_deadline)
//     [bookConfig?.content_deadline]
//   );

//   // Helper to remove any "All" or empty placeholder options from the lists
//   const sanitizeOptions = (optionsArray) => {
//     if (!Array.isArray(optionsArray)) return [];
//     return optionsArray.filter(opt => {
//       const val = String(opt?.value ?? "").toLowerCase();
//       const label = String(opt?.label ?? "").toLowerCase();
//       return val !== "" && val !== "all" && label !== "all";
//     });
//   };

//   // Fetch cartoons once. getCartoonsOnce() also protects against accidental
//   // component remounts, so changing a local filter will never refetch them.
//   useEffect(() => {
//     let isMounted = true;

//     const fetchAllCartoons = async () => {
//       setIsFetching(true);

//       try {
//         const fetched = await getCartoonsOnce();

//         if (!isMounted) return;

//         setAllCartoons(fetched);

//         if (fetched.length > 0 && !selectedImage) {
//           setSelectedImage(fetched[0]?.url || null);
//         }
//       } catch (error) {
//         if (isMounted) {
//           console.error('Failed to load cartoons:', error);
//         }
//       } finally {
//         if (isMounted) {
//           setIsFetching(false);
//         }
//       }
//     };

//     fetchAllCartoons();

//     return () => {
//       isMounted = false;
//     };
//   }, []);

//   // LOCAL FILTERING ONLY. No API request happens when params change.
//   const filteredCartoons = useMemo(() => {
//     if (allCartoons.length === 0) return [];

//     return allCartoons.filter((cartoon) => {
//       if (
//         params.ageGroup &&
//         cartoon.age_group &&
//         String(cartoon.age_group).toLowerCase() !== String(params.ageGroup).toLowerCase()
//       ) return false;

//       if (
//         params.gender !== "" &&
//         cartoon.gender !== undefined &&
//         Number(cartoon.gender) !== Number(params.gender)
//       ) return false;

//       if (
//         params.skinTone &&
//         cartoon.skin_tone &&
//         String(cartoon.skin_tone).toLowerCase() !== String(params.skinTone).toLowerCase()
//       ) return false;

//       if (
//         params.hairColor &&
//         cartoon.hair_color &&
//         String(cartoon.hair_color).toLowerCase() !== String(params.hairColor).toLowerCase()
//       ) return false;

//       if (
//         params.hairStyle &&
//         cartoon.hair_style &&
//         String(cartoon.hair_style).toLowerCase() !== String(params.hairStyle).toLowerCase()
//       ) return false;

//       if (
//         params.eyeColor &&
//         cartoon.eye_color &&
//         String(cartoon.eye_color).toLowerCase() !== String(params.eyeColor).toLowerCase()
//       ) return false;

//       if (
//         params.freckles &&
//         cartoon.freckles &&
//         String(cartoon.freckles).toLowerCase() !== String(params.freckles).toLowerCase()
//       ) return false;

//       return true;
//     });
//   }, [allCartoons, params]);

//   // Update only the preview when the local filtered list changes.
//   useEffect(() => {
//     if (allCartoons.length === 0) return;

//     if (filteredCartoons.length > 0 && filteredCartoons[0]?.url) {
//       setSelectedImage(filteredCartoons[0].url);
//       setIsAiRecommended(true);
//     } else {
//       setSelectedImage(null);
//     }
//   }, [filteredCartoons, allCartoons.length]);

//   const handleMatcherChange = (field, value) => {
//     if (isViewOnly) return;
//     setParams((prev) => ({ ...prev, [field]: value }));
//   };

//   const resetMatcher = () => {
//     if (isViewOnly) return;
//     setParams({
//       ageGroup: 'preschool',
//       gender: 1,
//       skinTone: '',
//       hairColor: '',
//       hairStyle: '',
//       eyeColor: '',
//       freckles: ''
//     });
//   };

//   // The Card Component that holds the static images from your constants
//   const OptionCard = ({
//     option,
//     value,
//     onSelect,
//     wide = false,
//     showImage = true,
//   }) => {
//     const selected = String(value ?? "").toLowerCase() === String(option.value ?? "").toLowerCase();
//     const staticImageUrl = option.image || null;

//     return (
//       <button
//         type="button"
//         onClick={() => onSelect(option.value)}
//         style={{
//           position: "relative",
//           minWidth: wide ? 130 : 80,
//           flex: wide ? "1 1 130px" : "1 1 0",
//           minHeight: wide ? 60 : 96,
//           padding: wide ? "8px 16px" : "10px 4px",
//           display: "flex",
//           flexDirection: wide ? "row" : "column",
//           alignItems: "center",
//           justifyContent: "center",
//           gap: wide ? 12 : 6,
//           borderRadius: 16,
//           border: `1.5px solid ${selected ? theme.primary : theme.border.default}`,
//           background: selected ? theme.primaryLight : theme.bg.elevated,
//           color: theme.text.primary,
//           cursor: "pointer",
//           transition: "all 0.15s ease",
//           boxShadow: selected ? "none" : "0 1px 2px rgba(0,0,0,0.02)",
//         }}
//       >
//         {showImage && (
//           <div
//             style={{
//               width: wide ? 40 : 48,
//               height: wide ? 40 : 48,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               flexShrink: 0,
//             }}
//           >
//             {staticImageUrl ? (
//               <img
//                 src={staticImageUrl}
//                 alt={option.label}
//                 style={{ width: '100%', height: '100%', objectFit: 'contain' }}
//               />
//             ) : (
//               <div style={{ width: 40, height: 40, background: theme.bg.hover, borderRadius: '50%' }} />
//             )}
//           </div>
//         )}

//         <div style={{ minWidth: 0, textAlign: "center" }}>
//           <div
//             style={{
//               fontSize: 12,
//               fontWeight: 600,
//               color: theme.text.primary,
//               lineHeight: 1.2,
//             }}
//           >
//             {option.label}
//           </div>
//           {option.subtitle && (
//             <div style={{ marginTop: 2, fontSize: 10, color: theme.text.muted }}>
//               {option.subtitle}
//             </div>
//           )}
//         </div>

//         {/* Checkmark fixed to top right */}
//         {selected && (
//           <span
//             style={{
//               position: "absolute",
//               top: -6,
//               right: -6,
//               width: 18,
//               height: 18,
//               borderRadius: "50%",
//               background: theme.primary,
//               color: "#fff",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               zIndex: 2,
//               border: "2px solid #fff"
//             }}
//           >
//             {/* Fallback text '✓' if lucide-react Check icon isn't imported */}
//             <span style={{ fontSize: 10, fontWeight: 'bold' }}>✓</span>
//           </span>
//         )}
//       </button>
//     );
//   };

//   const Section = ({ number, title, children, right }) => (
//     <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
//       <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//         <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
//           <span
//             style={{
//               width: 24,
//               height: 24,
//               borderRadius: "50%",
//               display: "inline-flex",
//               alignItems: "center",
//               justifyContent: "center",
//               background: theme.primary,
//               color: "#fff",
//               fontSize: 12,
//               fontWeight: 700,
//             }}
//           >
//             {number}
//           </span>
//           <span style={{ fontSize: 16, fontWeight: 700, color: theme.text.primary }}>
//             {title}
//           </span>
//         </div>
//         {right}
//       </div>
//       {children}
//     </div>
//   );

//   // Example dummy arrays (Replace these with your actual imported arrays!)
//   const DUMMY_AGE_GROUPS = [
//     { label: "Infant", subtitle: "(0-1)", value: "INFANT", image: "/path/to/infant.png" },
//     { label: "Toddler", subtitle: "(1-3)", value: "TODDLER", image: "/path/to/toddler.png" },
//     { label: "Preschool", subtitle: "(3-5)", value: "PRESCHOOL", image: "/path/to/preschool.png" },
//     { label: "Pre-K", subtitle: "(4-5)", value: "PREK", image: "/path/to/prek.png" },
//   ];

//   const DUMMY_GENDERS = [
//     { label: "Boy", value: 1, image: "/path/to/boy.png" },
//     { label: "Girl", value: 0, image: "/path/to/girl.png" },
//   ];

//   const DUMMY_SKIN_TONES = [
//     { label: 'Caucasian', value: 'CAUCASIAN', image: "/path/to/light.png" },
//     { label: 'Indian', value: 'INDIAN', image: "/path/to/light.png" },
//     { label: 'Asian', value: 'ASIAN', image: "/path/to/light.png" },
//     { label: 'African american', value: 'AFRICAN_AMERICAN', image: "/path/to/light.png" },
//     { label: 'Mixed', value: 'MIX', image: "/path/to/light.png" }
//   ];

//   const DUMMY_HAIR_COLORS = [
//     { label: 'Black', value: 'BLACK', image: "/path/to/light.png" },
//     { label: 'Brunette', value: 'BRUNETTE', image: "/path/to/light.png" },
//     { label: 'Blonde', value: 'BLONDE', image: "/path/to/light.png" },
//     { label: 'Brown', value: 'BROWN', image: "/path/to/light.png" }
//   ];

//   const DUMMY_HAIR_STYLES = [
//     { label: "Short", value: "SHORT", image: "/path/to/style-short.png" },
//     { label: "Long", value: "LONG", image: "/path/to/style-long.png" },
//   ];

//   const DUMMY_EYE_COLORS = [
//     { label: 'Brown', value: 'BROWN', image: "/path/to/eye-hazel.png" },
//     { label: 'Blue', value: 'BLUE', image: "/path/to/eye-hazel.png" },
//     { label: 'Hazel', value: 'HAZEL', image: "/path/to/eye-hazel.png" },
//     { label: 'Gray', value: 'GRAY', image: "/path/to/eye-hazel.png" }
//   ];

//   const DUMMY_FRECKLES = [
//     { label: "No", value: "no", image: "/path/to/freckles-no.png" },
//     { label: "Yes", value: "yes", image: "/path/to/freckles-yes.png" },
//   ];

//   return (
//     <div>
//       <div
//         ref={matcherRef}
//         className="visual-matcher-drawer"
//         style={{
//           display: "grid",
//           gridTemplateColumns: "minmax(210px, 280px) minmax(0, 1fr)",
//           gap: 32,
//           padding: 24,
//           borderRadius: 24,
//           background: theme.bg.elevated,
//           boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
//         }}
//       >
//         {/* COLUMN 1: Live Preview Card */}
//         <div
//           className="live-preview-column"
//           style={{
//             display: "flex",
//             flexDirection: "column",
//             position: "sticky",
//             top: 24,
//             height: "fit-content"
//           }}
//         >
//           <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
//             <span style={{ fontSize: 14, fontWeight: 700, color: theme.text.primary }}>
//               Live Preview
//             </span>
//             <button
//               type="button"
//               onClick={resetMatcher}
//               title="Reset Filters"
//               disabled={isViewOnly}
//               style={{
//                 background: "transparent",
//                 border: "none",
//                 color: isViewOnly ? theme.text.muted : theme.text.secondary,
//                 cursor: isViewOnly ? "not-allowed" : "pointer",
//                 display: "flex",
//                 alignItems: "center",
//                 gap: 4,
//                 fontSize: 12,
//               }}
//             >
//               Reset
//             </button>
//           </div>

//           <div
//             style={{
//               minHeight: 260,
//               borderRadius: 16,
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               overflow: "hidden",
//               position: "relative",
//               background: selectedImage
//                 ? `url(${selectedImage}) center/contain no-repeat`
//                 : theme.bg.base,
//               border: `1px solid ${theme.border.subtle}`,
//               opacity: isFetching ? 0.6 : 1,
//               transition: "opacity 0.2s ease"
//             }}
//           >
//             {/* AI Recommended Badge */}
//             {selectedImage && isAiRecommended && (
//               <div
//                 style={{
//                   position: 'absolute',
//                   top: 12,
//                   right: 12,
//                   background: '#FFB800',
//                   color: '#000',
//                   padding: '4px 10px',
//                   borderRadius: '12px',
//                   fontSize: 10,
//                   fontWeight: 700,
//                   boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
//                   zIndex: 3
//                 }}
//               >
//                 AI Match
//               </div>
//             )}

//             {/* Loading Indicator */}
//             {isFetching && (
//               <div style={{ position: 'absolute', zIndex: 10 }}>
//                 <span style={{ color: theme.primary, fontWeight: 'bold' }}>Loading...</span>
//               </div>
//             )}

//             {!selectedImage && !isFetching && (
//               <div style={{ color: theme.text.muted, fontSize: 12 }}>No match found</div>
//             )}
//           </div>
//         </div>

//         {/* COLUMN 2: Primary Attributes Form */}
//         <div style={{
//           display: "flex",
//           flexDirection: "column",
//           gap: 32,
//           minWidth: 0,
//           opacity: isViewOnly ? 0.6 : 1,
//           pointerEvents: isViewOnly ? 'none' : 'auto'
//         }}>
//           {/* 1. Age */}
//           <Section
//             number="1"
//             title="Age"
//             right={<span style={{ fontSize: 12, color: theme.primary, cursor: 'pointer', fontWeight: 500 }}>Why we ask? ⓘ</span>}
//           >
//             <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
//               {sanitizeOptions(DUMMY_AGE_GROUPS).map((option) => (
//                 <OptionCard
//                   key={String(option.value)}
//                   option={option}
//                   value={params.ageGroup}
//                   onSelect={(value) => handleMatcherChange("ageGroup", value)}
//                 />
//               ))}
//             </div>
//           </Section>

//           {/* 2. Boy or Girl */}
//           <Section number="2" title="Boy or Girl">
//             <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
//               {sanitizeOptions(DUMMY_GENDERS).map((option) => (
//                 <OptionCard
//                   key={String(option.value)}
//                   option={option}
//                   value={params.gender}
//                   onSelect={(value) => handleMatcherChange("gender", value)}
//                   wide
//                 />
//               ))}
//             </div>
//           </Section>

//           {/* 3. Skin Tone */}
//           <Section number="3" title="Skin tone">
//             <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
//               {sanitizeOptions(DUMMY_SKIN_TONES).map((option) => (
//                 <OptionCard
//                   key={String(option.value)}
//                   option={option}
//                   value={params.skinTone}
//                   onSelect={(value) => handleMatcherChange("skinTone", value)}
//                 />
//               ))}
//             </div>
//           </Section>

//           {/* 4. Hair (Nested Color & Style) */}
//           <Section number="4" title="Hair">
//             <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
//               {/* Hair Color */}
//               <div>
//                 <div style={{ fontSize: 13, color: theme.text.secondary, marginBottom: 8, fontWeight: 500 }}>
//                   Hair color
//                 </div>
//                 <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
//                   {sanitizeOptions(DUMMY_HAIR_COLORS).map((option) => (
//                     <OptionCard
//                       key={String(option.value)}
//                       option={option}
//                       value={params.hairColor}
//                       onSelect={(value) => handleMatcherChange("hairColor", value)}
//                     />
//                   ))}
//                 </div>
//               </div>

//               {/* Hair Style */}
//               <div>
//                 <div style={{ fontSize: 13, color: theme.text.secondary, marginBottom: 8, fontWeight: 500 }}>
//                   Hair style
//                 </div>
//                 <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
//                   {sanitizeOptions(DUMMY_HAIR_STYLES).map((option) => (
//                     <OptionCard
//                       key={String(option.value)}
//                       option={option}
//                       value={params.hairStyle}
//                       onSelect={(value) => handleMatcherChange("hairStyle", value)}
//                     />
//                   ))}
//                 </div>
//               </div>
//             </div>
//           </Section>

//           {/* More Options (Optional) */}
//           <Section number="5" title="Eye Color">
//             <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
//               {sanitizeOptions(DUMMY_EYE_COLORS).map((option) => (
//                 <OptionCard
//                   key={String(option.value)}
//                   option={option}
//                   value={params.eyeColor}
//                   onSelect={(value) => handleMatcherChange("eyeColor", value)}
//                 />
//               ))}
//             </div>
//           </Section>
//         </div>
//       </div>
//     </div>
//   );
// }

const normalizeGender = (value) => {
      if (value === null || value === undefined) return '';

      const v = String(value).trim().toLowerCase();

      if (['boy', 'male', 'm', '1'].includes(v)) return 'M';
      if (['girl', 'female', 'f', '0'].includes(v)) return 'F';

      return '';
    };

// ─── Child Details ───
export function PanelChild({ child, onSave, bookConfig }) {
  const toast = useToast();
  const [fname, setFname] = useState(child.first_name || '');
  const [lname, setLname] = useState(child.last_name || '');
  const [gender, setGender] = useState(
    normalizeGender(child.gender)
  );

  const isViewOnly = useMemo(
    () => isDeadlinePassed(bookConfig?.content_deadline),
    [bookConfig?.content_deadline]
  );
  const handleSave = async () => {
    if (isViewOnly) return;
    const result = await onSave({ first_name: fname, last_name: lname, gender });

    if (result?.ok) {
      toast.success('Child details saved.');
    } else {
      toast.error('Could not save child details. Please try again.');
    }
  };






  return (
    <Box>
      <SectionHeader
        title="Child Details"
        subtitle={
          isViewOnly
            ? "The deadline has passed. Child details are locked for printing."
            : `Update ${child.name}'s information for the yearbook.`
        }
      />

      <Box sx={{ maxWidth: 400 }}>
        <Box sx={{ mb: 2.75 }}>
          <Typography variant="overline" sx={{ mb: 0.75, display: 'block', color: 'text.secondary' }}>
            First Name
          </Typography>
          <TextField
            fullWidth
            value={fname}
            onChange={(e) => setFname(e.target.value)}
            disabled={isViewOnly}
          />
        </Box>

        <Box sx={{ mb: 2.75 }}>
          <Typography variant="overline" sx={{ mb: 0.75, display: 'block', color: 'text.secondary' }}>
            Last Name
          </Typography>
          <TextField
            fullWidth
            value={lname}
            onChange={(e) => setLname(e.target.value)}
            disabled={isViewOnly}
          />
        </Box>

        <Box sx={{ mb: 2.75 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
            Gender
          </Typography>

          <ToggleButtonGroup
            value={gender}
            exclusive
            onChange={(_, v) => {
              if (v !== null) setGender(v);
            }}
            disabled={isViewOnly}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1,

              '& .MuiToggleButtonGroup-grouped': {
                m: 0,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '12px !important',

                '&:not(:first-of-type)': {
                  borderLeft: '1px solid',
                  borderColor: 'grey.300',
                },
              },

              '& .MuiToggleButton-root': {
                py: 1.25,
                px: 1.5,
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'none',
                color: 'text.primary',
                bgcolor: '#fff',

                '&:hover': {
                  bgcolor: 'grey.50',
                },

                '&.Mui-selected': {
                  bgcolor: '#fff',
                  color: 'text.primary',
                  borderColor: '#000',
                  borderWidth: 2,

                  '&:hover': {
                    bgcolor: '#fff',
                  },
                },
              },
            }}
          >
            <ToggleButton value="F">Girl</ToggleButton>
            <ToggleButton value="M">Boy</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {!isViewOnly && (
          <Button variant="contained" onClick={handleSave}>
            Save Changes
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ─── Order & Delivery ───
function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function getOrderItem(order, projectId) {
  const candidates = [
    order?.item,
    ...(Array.isArray(order?.items) ? order.items : []),
    ...(Array.isArray(order?.orderItems) ? order.orderItems : []),
    ...(Array.isArray(order?.order_items) ? order.order_items : []),
  ].filter(Boolean);

  if (!candidates.length) return null;

  if (projectId) {
    const matching = candidates.find((item) =>
      String(firstDefined(item?.projectId, item?.project_id, '')) === String(projectId)
    );
    if (matching) return matching;
  }

  const requestedId = firstDefined(order?.order_item_id, order?.orderItemId, order?.itemId);
  if (requestedId) {
    const matching = candidates.find((item) =>
      String(firstDefined(item?.itemId, item?.item_id, item?.orderItemId, item?.order_item_id, item?.id, '')) === String(requestedId)
    );
    if (matching) return matching;
  }

  return candidates[0];
}

function normalizeDeliveryMethod(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'home' || raw.includes('home')) return 'home';
  if (raw === 'pickup' || raw === 'school' || raw.includes('school')) return 'school';
  return null;
}

function normalizeShippingAddress(raw = {}) {
  return {
    recipientName: firstDefined(raw?.recipientName, raw?.recipient_name, raw?.name, '') || '',
    line1: firstDefined(raw?.line1, raw?.address_line1, '') || '',
    line2: firstDefined(raw?.line2, raw?.address_line2, '') || '',
    city: raw?.city || '',
    state: firstDefined(raw?.state, raw?.region, '') || '',
    postalCode: firstDefined(raw?.postalCode, raw?.postal_code, raw?.zip, raw?.zip_code, '') || '',
    phone: raw?.phone || '',
    country: raw?.country || 'US',
  };
}

function coverKind(value) {
  const source = typeof value === 'object' && value !== null
    ? [
        value?.name,
        value?.label,
        value?.coverName,
        value?.cover_type,
        value?.coverType,
        value?.binding_type,
        value?.bindingType,
        value?.format_key,
        value?.formatKey,
        value?.skuCode,
        value?.sku_code,
        value?.metadata_json?.cover_type,
        value?.metadata_json?.binding_type,
      ].filter(Boolean).join(' ')
    : String(value || '');

  const text = source.toLowerCase();
  if (text.includes('saddle')) return 'saddle';
  if (text.includes('hard')) return 'hardcover';
  if (text.includes('soft') || text.includes('perfect')) return 'softcover';
  return null;
}

const COVER_RANK = { saddle: 0, softcover: 1, hardcover: 2 };
const COVER_LABEL = {
  saddle: 'Saddle Stitch',
  softcover: 'Softcover',
  hardcover: 'Hardcover',
};

function normalizePhysicalCoverOption(option) {
  if (!option) return null;

  const id = firstDefined(
    option?.catalogOfferSkuOptionId,
    option?.catalog_offer_sku_option_id,
    option?.coverId,
    option?.cover_id,
    option?.id
  );
  const kind = coverKind(option);
  if (!kind) return null;

  const priceCentsRaw = firstDefined(
    option?.priceCents,
    option?.price_cents,
    option?.unitPriceCents,
    option?.unit_price_cents,
    option?.price
  );
  let priceCents = null;
  if (priceCentsRaw !== undefined && priceCentsRaw !== null && priceCentsRaw !== '') {
    const numberValue = Number(priceCentsRaw);
    if (Number.isFinite(numberValue)) {
      // `price` is historically dollars; all explicit *Cents fields are cents.
      priceCents = option?.price !== undefined &&
        option?.priceCents === undefined &&
        option?.price_cents === undefined &&
        option?.unitPriceCents === undefined &&
        option?.unit_price_cents === undefined
        ? Math.round(numberValue * 100)
        : Math.round(numberValue);
    }
  }

  return {
    id: id ? String(id) : null,
    kind,
    name: firstDefined(option?.name, option?.label, option?.coverName, COVER_LABEL[kind]),
    priceCents,
    raw: option,
  };
}

function collectPhysicalCoverOptions(initialOrder, offer, currentItem) {
  const sources = [
    initialOrder?.physical_cover_options,
    initialOrder?.physicalCoverOptions,
    initialOrder?.available_cover_options,
    initialOrder?.availableCoverOptions,
    initialOrder?.available_covers,
    initialOrder?.availableCovers,
    initialOrder?.cover_pricing,
    initialOrder?.coverPricing,
    initialOrder?.pricing?.covers,
    initialOrder?.catalog?.covers,
    offer?.sku_options,
    offer?.skuOptions,
    offer?.covers,
  ];

  const normalized = sources
    .flatMap((value) => Array.isArray(value) ? value : [])
    .map(normalizePhysicalCoverOption)
    .filter(Boolean);

  const current = normalizePhysicalCoverOption({
    id: firstDefined(
      currentItem?.coverId,
      currentItem?.cover_id,
      currentItem?.catalogOfferSkuOptionId,
      currentItem?.catalog_offer_sku_option_id,
      initialOrder?.coverId,
      initialOrder?.cover_id,
      initialOrder?.catalogOfferSkuOptionId,
      initialOrder?.catalog_offer_sku_option_id
    ),
    name: firstDefined(
      currentItem?.coverName,
      currentItem?.cover_name,
      currentItem?.coverType,
      currentItem?.cover_type,
      currentItem?.bindingType,
      currentItem?.binding_type,
      currentItem?.formatKey,
      currentItem?.format_key,
      currentItem?.skuCode,
      currentItem?.sku_code,
      initialOrder?.coverName,
      initialOrder?.cover_name,
      initialOrder?.coverType,
      initialOrder?.cover_type,
      initialOrder?.bindingType,
      initialOrder?.binding_type,
      initialOrder?.formatKey,
      initialOrder?.format_key,
      initialOrder?.skuCode,
      initialOrder?.sku_code
    ),
    priceCents: firstDefined(
      currentItem?.unitPriceCents,
      currentItem?.unit_price_cents,
      currentItem?.unitPrice,
      currentItem?.unit_price,
      initialOrder?.unit_price_cents,
      initialOrder?.unitPriceCents
    ),
  });

  const byKind = new Map();
  normalized.forEach((option) => {
    const existing = byKind.get(option.kind);
    if (!existing || (!existing.id && option.id)) byKind.set(option.kind, option);
  });

  if (current?.kind && !byKind.has(current.kind)) {
    byKind.set(current.kind, current);
  }

  return Array.from(byKind.values()).sort(
    (a, b) => (COVER_RANK[a.kind] ?? 99) - (COVER_RANK[b.kind] ?? 99)
  );
}

function addressEquals(a, b) {
  return ['recipientName', 'line1', 'line2', 'city', 'state', 'postalCode', 'phone', 'country']
    .every((key) => String(a?.[key] || '').trim() === String(b?.[key] || '').trim());
}

function makeAmendmentIdempotencyKey(orderId) {
  const suffix = globalThis?.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `amendment_${String(orderId || 'order').slice(0, 24)}_${suffix}`.slice(0, 128);
}

function formatMoney(cents, currency = 'usd') {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'usd').toUpperCase(),
  }).format(value / 100);
}

const ORDER_AMENDMENT_ERROR_MESSAGES = {
  // Refund-required changes
  cover_downgrade_requires_refund_flow:
    'Changing to a lower-priced cover is not possible.',

  quantity_decrease_requires_refund_flow:
    'Reducing the number of copies is not possible.',

  item_downgrade_requires_refund_flow:
    'This change is not available here.',

  delivery_downgrade_requires_refund_flow:
    'This delivery change is not available here.',

  negative_amendment_requires_refund_flow:
    'This change is not available here.',

  // Address / delivery
  shipping_address_required_for_home:
    'Please enter a shipping address for home delivery.',

  shipping_recipient_name_required:
    'Please enter the recipient name for home delivery.',

  complete_shipping_address_required:
    'Please complete the shipping address before continuing.',

  shipping_method_required_for_home:
    'Home delivery is not currently available for this order.',

  delivery_method_must_be_pickup_or_home:
    'Please choose school delivery or home delivery.',

  // Order state
  amendment_has_no_changes:
    'There are no changes to save.',

  order_item_not_found:
    'We could not find this yearbook in your order. Please refresh and try again.',

  order_item_has_no_provisioned_project:
    'This yearbook is not ready to be updated yet. Please try again shortly.',

  order_item_missing_catalog_snapshot:
    'We could not load the current cover information. Please refresh and try again.',

  order_has_no_active_items:
    'We could not find an active yearbook on this order.',

  order_has_no_provisioned_projects:
    'Your yearbook is still being prepared. Please try again shortly.',

  order_authorization_required:
    'You do not have access to update this order.',

  order_is_not_parent_storefront:
    'This order cannot be changed from this screen.',

  // Idempotency / payment
  idempotency_key_used_for_different_amendment:
    'This update could not be completed safely. Please refresh and try again.',

  stripe_client_secret_missing:
    'We could not start the payment. Please try again.',

  unsupported_payment_provider:
    'We could not process this payment. Please try again.',

  payment_is_not_storefront_order_amendment:
    'We could not match this payment to your order update.',

  amendment_payment_missing_plan:
    'We could not complete this order update. Please contact us if the payment was already made.',
};

function getOrderAmendmentErrorMessage(error) {
  const detail = error?.detail;

  const candidates = [
    error?.code,
    typeof detail === 'string' ? detail : null,
    typeof detail === 'object' ? detail?.code : null,
    error?.message,
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (ORDER_AMENDMENT_ERROR_MESSAGES[candidate]) {
      return ORDER_AMENDMENT_ERROR_MESSAGES[candidate];
    }
  }

  // If backend supplied an already-readable message, preserve it.
  if (
    typeof detail === 'object' &&
    detail?.message &&
    !String(detail.message).includes('_')
  ) {
    return detail.message;
  }

  if (
    error?.message &&
    !String(error.message).includes('_')
  ) {
    return error.message;
  }

  return 'We could not update your order. Please try again.';
}

export function PanelOrder({ projectId, initialOrder, offer, onRefresh }) {
  const toast = useToast();
  const [sourceOrder, setSourceOrder] = useState(initialOrder || null);
  const [defaultAddressLoaded, setDefaultAddressLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompletingPayment, setIsCompletingPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [submittedRequest, setSubmittedRequest] = useState(null);

  useEffect(() => {
    setSourceOrder(initialOrder || null);
    setPaymentResponse(null);
    setSubmittedRequest(null);
    setErrorMessage(null);
    setDefaultAddressLoaded(false);
  }, [initialOrder, projectId]);

  const currentItem = useMemo(
    () => getOrderItem(sourceOrder, projectId),
    [sourceOrder, projectId]
  );

  const orderId = firstDefined(
    sourceOrder?.publicOrderId,
    sourceOrder?.public_order_id,
    sourceOrder?.orderId,
    sourceOrder?.order_id,
    sourceOrder?.id
  );

  const itemId = firstDefined(
    currentItem?.itemId,
    currentItem?.item_id,
    currentItem?.orderItemId,
    currentItem?.order_item_id,
    currentItem?.id,
    sourceOrder?.order_item_id,
    sourceOrder?.orderItemId,
    sourceOrder?.itemId
  );

  const currentQuantity = Math.max(
    1,
    Number(firstDefined(currentItem?.quantity, sourceOrder?.quantity, 1)) || 1
  );

  const coverOptions = useMemo(
    () => collectPhysicalCoverOptions(sourceOrder, offer, currentItem),
    [sourceOrder, offer, currentItem]
  );

  const currentCoverId = firstDefined(
    currentItem?.coverId,
    currentItem?.cover_id,
    currentItem?.catalogOfferSkuOptionId,
    currentItem?.catalog_offer_sku_option_id,
    sourceOrder?.coverId,
    sourceOrder?.cover_id,
    sourceOrder?.catalogOfferSkuOptionId,
    sourceOrder?.catalog_offer_sku_option_id
  );

  const currentCoverKind = coverKind({
    name: firstDefined(
      currentItem?.coverName,
      currentItem?.cover_name,
      currentItem?.coverType,
      currentItem?.cover_type,
      currentItem?.bindingType,
      currentItem?.binding_type,
      currentItem?.formatKey,
      currentItem?.format_key,
      currentItem?.skuCode,
      currentItem?.sku_code,
      sourceOrder?.coverName,
      sourceOrder?.cover_name,
      sourceOrder?.coverType,
      sourceOrder?.cover_type,
      sourceOrder?.bindingType,
      sourceOrder?.binding_type,
      sourceOrder?.formatKey,
      sourceOrder?.format_key,
      sourceOrder?.skuCode,
      sourceOrder?.sku_code
    ),
  }) || coverOptions.find((option) => String(option.id) === String(currentCoverId || ''))?.kind || null;

  const deliveryObject = firstDefined(sourceOrder?.delivery, sourceOrder?.shipping, {}) || {};
  const explicitDelivery = normalizeDeliveryMethod(firstDefined(
    deliveryObject?.method,
    sourceOrder?.deliveryMethod,
    sourceOrder?.delivery_method,
    sourceOrder?.shipTo,
    sourceOrder?.ship_to,
    sourceOrder?.shipping_method,
    sourceOrder?.checkoutContext?.deliveryMethod,
    sourceOrder?.checkout_context?.delivery_method
  ));
  const rawOrderAddress = firstDefined(
    deliveryObject?.address,
    sourceOrder?.shippingAddress,
    sourceOrder?.shipping_address,
    sourceOrder?.address
  );
  const currentAddress = normalizeShippingAddress(rawOrderAddress || {});
  const currentDelivery = explicitDelivery || (
    rawOrderAddress || Number(firstDefined(sourceOrder?.shippingCents, sourceOrder?.shipping_cents, 0)) > 0
      ? 'home'
      : 'school'
  );

  const [quantity, setQuantity] = useState(currentQuantity);
  const [selectedCoverId, setSelectedCoverId] = useState(currentCoverId ? String(currentCoverId) : '');
  const [shipTo, setShipTo] = useState(currentDelivery);
  const [shippingAddress, setShippingAddress] = useState(currentAddress);

  useEffect(() => {
    setQuantity(currentQuantity);
    setSelectedCoverId(currentCoverId ? String(currentCoverId) : '');
    setShipTo(currentDelivery);
    setShippingAddress(currentAddress);
    // Re-hydrate only when the authoritative order changes.
  }, [orderId, itemId, currentQuantity, currentCoverId, currentDelivery, sourceOrder]);

  // School-delivery orders do not have an order shipping address. Prefill the
  // home-upgrade form from the parent's saved default address when available.
  useEffect(() => {
    if (defaultAddressLoaded || currentDelivery === 'home' || rawOrderAddress) return;
    let active = true;

    api.getDefaultShippingAddress()
      .then((address) => {
        if (!active || !address) return;
        setShippingAddress((existing) => {
          const hasTypedAddress = Boolean(existing.line1 || existing.city || existing.postalCode);
          return hasTypedAddress ? existing : normalizeShippingAddress(address);
        });
      })
      .catch((err) => {
        if (err?.status !== 404) console.warn('Could not load default shipping address', err);
      })
      .finally(() => {
        if (active) setDefaultAddressLoaded(true);
      });

    return () => { active = false; };
  }, [currentDelivery, defaultAddressLoaded, rawOrderAddress]);

  const selectedCover = coverOptions.find((option) => String(option.id) === String(selectedCoverId));
  const selectedCoverKind = selectedCover?.kind || currentCoverKind;
  const currentRank = COVER_RANK[currentCoverKind];
  const selectedRank = COVER_RANK[selectedCoverKind];

  const quantityChanged = quantity !== currentQuantity;
  const coverChanged = Boolean(
    selectedCoverId && (
      (currentCoverId && String(selectedCoverId) !== String(currentCoverId)) ||
      (!currentCoverId && selectedCoverKind && selectedCoverKind !== currentCoverKind)
    )
  );
  const deliveryChanged = shipTo !== currentDelivery;
  const addressChanged = shipTo === 'home' && !addressEquals(shippingAddress, currentAddress);
  const hasChanges = quantityChanged || coverChanged || deliveryChanged || addressChanged;

  const configurationComplete = Boolean(orderId && itemId && currentCoverKind);
  const coverUpgradeDataComplete = coverOptions.some((option) => option.id && (COVER_RANK[option.kind] ?? -1) > (currentRank ?? -1));

  const updateAddress = (field, value) => {
    setShippingAddress((previous) => ({ ...previous, [field]: value }));
    setPaymentResponse(null);
    setSubmittedRequest(null);
  };

  const resetPendingPayment = () => {
    if (paymentResponse) {
      setPaymentResponse(null);
      setSubmittedRequest(null);
    }
  };

  const setDraftQuantity = (next) => {
    const normalized = Math.min(20, Math.max(currentQuantity, Number(next) || currentQuantity));
    resetPendingPayment();
    setQuantity(normalized);
  };

  const setDraftCover = (option) => {
    if (!option?.id) return;
    const optionRank = COVER_RANK[option.kind];
    if (currentRank !== undefined && optionRank < currentRank) return;
    resetPendingPayment();
    setSelectedCoverId(String(option.id));
  };

  const setDraftDelivery = (next) => {
    // Home -> pickup reduces the paid order and belongs in a refund flow.
    if (currentDelivery === 'home' && next === 'school') return;
    resetPendingPayment();
    setShipTo(next);
  };

  const validate = () => {
    if (!orderId || !itemId) {
      setErrorMessage('This order is missing the order/item identifiers needed for amendments.');
      return false;
    }

    if (!currentCoverKind) {
      setErrorMessage('The dashboard response does not identify the current physical cover.');
      return false;
    }

    if (quantity < currentQuantity) {
      setErrorMessage(
        'Reducing the number of copies requires a refund. Please contact us to make this change.'
      );
      return false;
    }

    if (coverChanged && !selectedCover?.id) {
      setErrorMessage('The selected cover is missing its catalog cover ID.');
      return false;
    }

    if (
      currentRank !== undefined &&
      selectedRank !== undefined &&
      selectedRank < currentRank
    ) {
      setErrorMessage(
        'Changing to a lower-priced cover requires a refund. Please contact us to make this change.'
      );
      return false;
    }

    if (shipTo === 'home') {
      const required = [
        ['recipientName', 'Recipient name'],
        ['line1', 'Street address'],
        ['city', 'City'],
        ['state', 'State'],
        ['postalCode', 'ZIP code'],
      ];
      const missing = required.find(([key]) => !String(shippingAddress[key] || '').trim());
      if (missing) {
        setErrorMessage(`${missing[1]} is required for home delivery.`);
        return false;
      }
    }

    if (!hasChanges) {
      setErrorMessage('Make a change before saving.');
      return false;
    }

    return true;
  };

  const buildAmendmentRequest = () => {
    const payload = {
      itemId: String(itemId),
      idempotencyKey: makeAmendmentIdempotencyKey(orderId),
    };

    if (quantityChanged) payload.quantity = quantity;
    if (coverChanged) payload.coverId = String(selectedCoverId);

    if (deliveryChanged || addressChanged) {
      payload.delivery = {
        method: shipTo === 'home' ? 'home' : 'pickup',
      };

      if (shipTo === 'home') {
        payload.delivery.address = {
          recipientName: shippingAddress.recipientName.trim(),
          line1: shippingAddress.line1.trim(),
          line2: shippingAddress.line2.trim() || null,
          city: shippingAddress.city.trim(),
          state: shippingAddress.state.trim(),
          postalCode: shippingAddress.postalCode.trim(),
          country: shippingAddress.country || 'US',
          phone: shippingAddress.phone.trim() || null,
        };
      }
    }

    return payload;
  };

  const applyCompletedAmendment = async (response) => {
    if (response?.order) {
      setSourceOrder((previous) => ({
        ...response.order,
        // Amendment responses serialize the updated order but do not include
        // the dashboard-only catalog choices. Preserve them until refresh.
        physical_cover_options:
          previous?.physical_cover_options || previous?.physicalCoverOptions || [],
      }));
    }
    setPaymentResponse(null);
    setSubmittedRequest(null);
    toast.success('Order & delivery updated.');
    if (onRefresh) await onRefresh({ showLoading: false });
  };

  const handleSave = async () => {
    setErrorMessage(null);
    if (!validate()) return;

    const payload = buildAmendmentRequest();
    setIsSubmitting(true);

    try {
      const response = await api.createOrderAmendment(orderId, payload);

      if (response?.applied && !response?.requiresPayment) {
        await applyCompletedAmendment(response);
        return;
      }

      if (!response?.requiresPayment || !response?.clientSecret) {
        throw new Error('The amendment response did not include a payable Stripe client secret.');
      }

      setSubmittedRequest(payload);
      setPaymentResponse(response);
    } catch (err) {
      console.error('Could not create order amendment', err);

      const message = getOrderAmendmentErrorMessage(err);

      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const waitForWebhookApplication = async () => {
    if (!submittedRequest) throw new Error('Missing amendment request for payment verification.');

    for (let attempt = 0; attempt < 15; attempt += 1) {
      const response = await api.createOrderAmendment(orderId, submittedRequest);
      if (response?.applied || String(response?.status || '').toLowerCase() === 'applied') {
        return response;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    throw new Error('Payment succeeded, but the order update is still processing. Refresh in a few seconds.');
  };

  const handlePaymentComplete = async () => {
    setIsCompletingPayment(true);
    setErrorMessage(null);

    try {
      const appliedResponse = await waitForWebhookApplication();
      await applyCompletedAmendment(appliedResponse);
    } catch (err) {
      const message = err?.message || 'Payment succeeded, but the order has not refreshed yet.';
      setErrorMessage(message);
      toast.info(message);
    } finally {
      setIsCompletingPayment(false);
    }
  };

  if (!sourceOrder) {
    return (
      <Box sx={{ maxWidth: 640 }}>
        <SectionHeader title="Order & Delivery" subtitle="Manage copies, cover, and delivery for your yearbook." />
        <Alert severity="info">No yearbook order was found for this project.</Alert>
      </Box>
    );
  }

  if (paymentResponse) {
    return (
      <Box sx={{ maxWidth: 560 }}>
        <SectionHeader
          title="Complete Your Changes"
          subtitle="Only the additional amount for this order change is being charged."
        />

        {errorMessage && <Alert severity="warning" sx={{ mb: 2 }}>{errorMessage}</Alert>}

        <Box sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>Additional amount due</Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {formatMoney(paymentResponse.amountCents, paymentResponse.currency)}
            </Typography>
          </Box>
          <Typography sx={{ mt: 0.75, fontSize: 12, color: 'text.secondary' }}>
            Your original paid order remains unchanged unless this payment succeeds.
          </Typography>
        </Box>

        <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <StripePaymentWrapper
            clientSecret={paymentResponse.clientSecret}
            amountCents={paymentResponse.amountCents}
            currency={paymentResponse.currency || 'usd'}
            onComplete={handlePaymentComplete}
            processing={isCompletingPayment}
          />
        </Box>

        <Typography sx={{ mt: 1.5, fontSize: 11.5, color: 'text.secondary', textAlign: 'center' }}>
          Complete or retry this payment before creating a different paid order change.
        </Typography>
      </Box>
    );
  }

  const coverDisplayDelta = (() => {
    if (!coverChanged || currentRank === undefined || selectedRank === undefined) return null;
    const currentOption = coverOptions.find((option) => option.kind === currentCoverKind);
    if (Number.isFinite(currentOption?.priceCents) && Number.isFinite(selectedCover?.priceCents)) {
      return Math.max(0, selectedCover.priceCents - currentOption.priceCents);
    }
    // Display hint only. The backend computes and returns the authoritative charge.
    return Math.max(0, selectedRank - currentRank) * 500;
  })();

  const addedCopies = Math.max(0, quantity - currentQuantity);
  const onlyFreeAddressEdit = hasChanges &&
    currentDelivery === 'home' &&
    shipTo === 'home' &&
    addressChanged &&
    !quantityChanged &&
    !coverChanged;

  return (
    <Box>
      <SectionHeader
        title="Order & Delivery"
        subtitle="Add copies, upgrade the cover, or change how your yearbook is delivered."
      />

      <Box sx={{ maxWidth: 640 }}>
        {errorMessage && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{errorMessage}</Alert>}

        {!configurationComplete && (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            This order loaded, but the dashboard response is missing the current physical cover or item details. We will not guess the purchased configuration.
          </Alert>
        )}

        <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1.5 }}>Copies</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setDraftQuantity(quantity - 1)}
            disabled={quantity <= currentQuantity || isSubmitting}
            sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '50%', fontSize: 20, p: 0 }}
          >−</Button>
          <Typography sx={{ minWidth: 40, textAlign: 'center', fontSize: 34, fontWeight: 800 }}>{quantity}</Typography>
          <Button
            variant="outlined"
            onClick={() => setDraftQuantity(quantity + 1)}
            disabled={quantity >= 20 || isSubmitting}
            sx={{ minWidth: 44, width: 44, height: 44, borderRadius: '50%', fontSize: 20, p: 0 }}
          >+</Button>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {currentQuantity} {currentQuantity === 1 ? 'copy' : 'copies'} already purchased
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>Cover</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
            You can upgrade a paid order, but downgrades require a refund flow.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
            {['saddle', 'softcover', 'hardcover'].map((kind) => {
              const option = coverOptions.find((candidate) => candidate.kind === kind);
              const rank = COVER_RANK[kind];
              const isCurrent = kind === currentCoverKind;
              const isSelected = isCurrent ? !coverChanged : String(option?.id || '') === String(selectedCoverId || '');
              const isDowngrade = currentRank !== undefined && rank < currentRank;
              const isUnavailableUpgrade = !isCurrent && rank > (currentRank ?? -1) && !option?.id;
              const disabled = isDowngrade || isUnavailableUpgrade || isSubmitting || !currentCoverKind;
              const deltaSteps = currentRank === undefined ? 0 : Math.max(0, rank - currentRank);

              return (
                <Button
                  key={kind}
                  variant={isSelected ? 'contained' : 'outlined'}
                  disabled={disabled}
                  onClick={() => option && setDraftCover(option)}
                  sx={{ minHeight: 76, py: 1.25, px: 1.5, textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{COVER_LABEL[kind]}</Typography>
                    <Typography sx={{ fontSize: 10.5, opacity: 0.78, mt: 0.35 }}>
                      {isCurrent
                        ? 'Current cover'
                        : isDowngrade
                          ? 'Downgrade unavailable'
                          : isUnavailableUpgrade
                            ? 'Upgrade option unavailable'
                            : Number.isFinite(option?.priceCents) && coverOptions.find((x) => x.kind === currentCoverKind)?.priceCents != null
                              ? `Upgrade ${formatMoney(Math.max(0, option.priceCents - coverOptions.find((x) => x.kind === currentCoverKind).priceCents))}`
                              : `Upgrade +${formatMoney(deltaSteps * 500)}`}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Box>

          {!coverUpgradeDataComplete && currentCoverKind !== 'hardcover' && (
            <Typography sx={{ mt: 1, fontSize: 11, color: 'text.secondary' }}>
              Upgrade buttons become active when the dashboard supplies the catalog cover option IDs.
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>Delivery</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
            Home delivery is a $7.99 upgrade from school delivery. Updating an existing home address is free.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
            <Button
              variant={shipTo === 'school' ? 'contained' : 'outlined'}
              disabled={currentDelivery === 'home' || isSubmitting}
              onClick={() => setDraftDelivery('school')}
              sx={{ minHeight: 72, py: 1.25, px: 1.5, textTransform: 'none', justifyContent: 'flex-start' }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>School Delivery</Typography>
                <Typography sx={{ fontSize: 10.5, opacity: 0.78 }}>
                  {currentDelivery === 'school' ? 'Current delivery · FREE' : 'Downgrade unavailable'}
                </Typography>
              </Box>
            </Button>

            <Button
              variant={shipTo === 'home' ? 'contained' : 'outlined'}
              disabled={isSubmitting}
              onClick={() => setDraftDelivery('home')}
              sx={{ minHeight: 72, py: 1.25, px: 1.5, textTransform: 'none', justifyContent: 'flex-start' }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Home Delivery</Typography>
                <Typography sx={{ fontSize: 10.5, opacity: 0.78 }}>
                  {currentDelivery === 'home' ? 'Current delivery · address updates free' : 'Upgrade +$7.99'}
                </Typography>
              </Box>
            </Button>
          </Box>
        </Box>

        {shipTo === 'home' && (
          <Box sx={{ mb: 4, p: 2.25, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>Shipping Address</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 2 }}>
              {currentDelivery === 'home'
                ? 'Edit the address at no additional delivery charge.'
                : 'Enter the address for your home-delivery upgrade.'}
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['recipientName', 'Recipient Name', 12],
                ['line1', 'Street Address', 12],
                ['line2', 'Apt / Suite', 6],
                ['city', 'City', 6],
                ['state', 'State', 6],
                ['postalCode', 'ZIP Code', 6],
                ['phone', 'Phone', 12],
              ].map(([key, label, xs]) => (
                <Grid item xs={12} sm={xs} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={label}
                    value={shippingAddress[key] || ''}
                    disabled={isSubmitting}
                    onChange={(event) => updateAddress(key, event.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box sx={{ p: 2.5, bgcolor: '#fafafa', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 1.5 }}>Changes to your order</Typography>

          {!hasChanges ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No changes yet.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              {coverChanged && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: 12.5 }}>{COVER_LABEL[currentCoverKind]} → {COVER_LABEL[selectedCoverKind]}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                    {coverDisplayDelta != null ? `+${formatMoney(coverDisplayDelta * currentQuantity)}` : 'Calculated at checkout'}
                  </Typography>
                </Box>
              )}
              {addedCopies > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: 12.5 }}>Add {addedCopies} {addedCopies === 1 ? 'copy' : 'copies'} · {COVER_LABEL[selectedCoverKind] || 'selected cover'}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                    {Number.isFinite(selectedCover?.priceCents) ? `+${formatMoney(selectedCover.priceCents * addedCopies)}` : 'Calculated at checkout'}
                  </Typography>
                </Box>
              )}
              {deliveryChanged && shipTo === 'home' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: 12.5 }}>Home Delivery</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>+$7.99</Typography>
                </Box>
              )}
              {addressChanged && !deliveryChanged && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontSize: 12.5 }}>Update shipping address</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>FREE</Typography>
                </Box>
              )}
              {!onlyFreeAddressEdit && (
                <Typography sx={{ pt: 0.5, fontSize: 10.5, color: 'text.secondary' }}>
                  Displayed upgrade amounts are estimates. The backend calculates the authoritative delta and any tax before Stripe payment.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          sx={{ mt: 2.5, py: 1.35, fontWeight: 800 }}
          onClick={handleSave}
          disabled={!hasChanges || !configurationComplete || isSubmitting}
        >
          {isSubmitting
            ? 'Preparing Changes...'
            : onlyFreeAddressEdit
              ? 'Save Address'
              : 'Continue to Payment'}
        </Button>
      </Box>
    </Box>
  );
}
